import { difference, groupBy, intersection } from 'ramda'
import type { EntityInfo, EntityInstance, StaticEntityInfo } from '../types.js'
import { BoInfoSymbol } from '../consts.js'
import { equal } from './data-util.js'
import { PairwiseSet } from '../data/pairwise-set.js'
import { PairwiseMap } from '../data/pairwise-map.js'

type Format = 'text' | 'html'
type Action = 'add' | 'remove' | 'change'
const actionNames: Record<Action, Record<Format, string>> = {
	add: {
		text: 'Added',
		html: '<span class="diff-action-add">+</span>',
	},
	remove: {
		text: 'Removed',
		html: '<span class="diff-action-remove">✕</span>',
	},
	change: {
		text: 'Changed',
		html: '<span class="diff-action-change">✎</span>',
	},
}
const indentations: Record<Format, (text: string) => string> = {
	text: (text) => `\xa0\xa0${text}`,
	html: (text) => `<div class="diff-indent">${text}</div>`,
}
const joins: Record<Format, (texts: string[]) => string> = {
	text: (texts) => texts.join('\n'),
	html: (texts) => `<div class="diff-lines">${texts.join('')}</div>`,
}
const keyFormatters: Record<Format, (key: string | number) => string> = {
	text: (key) => key.toString(),
	html: (key) => `<span class="diff-key">${key}</span>`,
}
const valueFormatters: Record<Format, (value: any) => string> = {
	text: (value) => JSON.stringify(value),
	html: (value) =>
		`<span class="diff-value">${JSON.stringify(value)?.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</span>`,
}

export class Difference {
	constructor(
		public path: (string | number)[],
		public oldValue: any,
		public newValue: any,
		public action: 'add' | 'remove' | 'change',
		public isOfInterest: boolean,
		public isComputed: boolean,
	) {}

	toString(format: 'text' | 'html' = 'text', onlyLastPathPart = false): string {
		const pathToConsider = onlyLastPathPart ? this.path.slice(-1) : this.path
		const userFriendlyPath = pathToConsider
			.map((key) => (typeof key === 'number' ? `#${key + 1}` : key))
			.join(' → ')

		const oldValueStr = valueFormatters[format](this.oldValue)
		const newValueStr = valueFormatters[format](this.newValue)

		const actionName = actionNames[this.action][format]
		let result: string
		if (this.action === 'add') {
			result = `${keyFormatters[format](userFriendlyPath)}: ${newValueStr}`
		} else if (this.action === 'remove') {
			result = `${keyFormatters[format](userFriendlyPath)}: ${oldValueStr}`
		} else {
			result = `${keyFormatters[format](userFriendlyPath)} from ${oldValueStr} to ${newValueStr}`
		}
		if (format === 'html') {
			result = `<span class="diff-line"><span class="diff-action">${actionName}</span> <span class="diff-text">${result}</span></span>`
		} else {
			result = `${actionName} ${result}`
		}
		return result
	}

	static formatListOfDifferences(
		differences: Difference[],
		format: 'text' | 'html' = 'text',
		formatFn: (diff: Difference) => string = (diff) =>
			diff.toString(format, true),
	) {
		function formatLevel(diffs: Difference[], level: number): string[] {
			const diffsAtLevel = diffs.filter(
				(diff) => diff.path.length === level + 1,
			)
			const subDiffs = diffs.filter((diff) => diff.path.length > level + 1)
			const groupedSubDiffs = groupBy(
				(diff) => diff.path[level].toString(),
				subDiffs,
			)

			const subEntries = Object.entries(groupedSubDiffs) as [
				string,
				Difference[],
			][]
			const subLines = subEntries.flatMap(([key, subDiffs]) => {
				const subLinesForKey = formatLevel(subDiffs, level + 1)
				return [
					keyFormatters[format](key),
					...subLinesForKey.map((line) => indentations[format](line)),
				]
			})

			const lines = [...diffsAtLevel.map((diff) => formatFn(diff)), ...subLines]
			return lines
		}

		const formattedLines = formatLevel(differences, 0)
		return joins[format](formattedLines)
	}
}

export interface CalculateDifferenceOptions {
	/** @default true */
	includeComputedProperties?: boolean
	/** @default false */
	includeNotOfInterestProperties?: boolean
	/** @default true */
	recurseIntoObjects?: boolean
	// ignoreSpecificProperties?:
}

function getProperties(
	obj: any,
	options: Required<CalculateDifferenceOptions>,
): {
	key: string | number
	isOfInterest: boolean
	isComputed: boolean
}[] {
	if (!obj) return []
	if (obj?.[BoInfoSymbol]) {
		const boInfo: EntityInfo | StaticEntityInfo = obj[BoInfoSymbol]

		const allProperties = boInfo.allPropertyNames.map((name) => ({
			key: name,
			isOfInterest: boInfo.propertyNamesOfInterest.includes(name),
			isComputed: boInfo.computedPropertyNames.includes(name),
		}))

		const filteredProperties = allProperties.filter((prop) => {
			if (!options.includeComputedProperties && prop.isComputed) return false
			if (!options.includeNotOfInterestProperties && !prop.isOfInterest)
				return false
			return true
		})
		return filteredProperties
	}

	let keys = Object.keys(obj) as (string | number)[]
	if (Array.isArray(obj)) keys = keys.map((key) => parseInt(key as string))
	return keys.map((key) => ({
		key,
		isOfInterest: true,
		isComputed: false,
	}))
}

export function calculateObjectDifferences(
	a: any,
	b: any,
	options?: CalculateDifferenceOptions,
	alreadyDiffedObjects = new PairwiseSet<any>(),
): Difference[] {
	const alreadyComparedObjects = new PairwiseMap<any, boolean | undefined>()

	function calculateObjectDifferencesInternal(
		a: any,
		b: any,
		pathPrefix: (string | number)[],
		options: Required<CalculateDifferenceOptions>,
	): Difference[] {
		if (typeof a === 'object' && typeof b === 'object') {
			if (alreadyDiffedObjects.hasPair(a, b)) {
				return []
			}
			alreadyDiffedObjects.addPair(a, b)
		}

		const propertiesA = getProperties(a, options)
		const propertiesB = getProperties(b, options)
		const propertiesOnlyInA = difference(propertiesA, propertiesB)
		const propertiesOnlyInB = difference(propertiesB, propertiesA)
		const commonKeys = intersection(propertiesA, propertiesB)

		const differences: Difference[] = []
		for (const p of propertiesOnlyInA) {
			const { key } = p
			const path = [...pathPrefix, key]
			if (options.recurseIntoObjects && a[key] && typeof a[key] === 'object') {
				differences.push(
					...calculateObjectDifferencesInternal(
						a[key],
						undefined,
						path,
						options,
					),
				) // Recurse into objects
			} else {
				if (!a[key]) continue
				differences.push(
					new Difference(
						path,
						a[key],
						undefined,
						'remove',
						p.isOfInterest,
						p.isComputed,
					),
				)
			}
		}
		for (const p of propertiesOnlyInB) {
			const { key } = p
			const path = [...pathPrefix, key]
			if (options.recurseIntoObjects && b[key] && typeof b[key] === 'object') {
				differences.push(
					...calculateObjectDifferencesInternal(
						undefined,
						b[key],
						path,
						options,
					),
				) // Recurse into objects
			} else {
				if (!b[key]) continue
				differences.push(
					new Difference(
						path,
						undefined,
						b[key],
						'add',
						p.isOfInterest,
						p.isComputed,
					),
				)
			}
		}
		for (const p of commonKeys) {
			const { key } = p
			const path = [...pathPrefix, key]
			const valueA = a[key]
			const valueB = b[key]

			// if (typeof valueA == 'function' && typeof valueB == 'function') continue

			if (equal(valueA, valueB, undefined, alreadyComparedObjects)) {
				continue // No difference
			}

			const aIsObject = typeof valueA === 'object' && valueA !== null
			const bIsObject = typeof valueB === 'object' && valueB !== null
			if (
				(aIsObject && bIsObject) ||
				(options.recurseIntoObjects && (aIsObject || bIsObject))
			) {
				differences.push(
					...calculateObjectDifferencesInternal(
						valueA ?? {},
						valueB ?? {},
						path,
						options,
					),
				) // Recurse into objects
			} else {
				differences.push(
					new Difference(
						path,
						valueA,
						valueB,
						'change',
						p.isOfInterest,
						p.isComputed,
					),
				)
			}
		}
		return differences
	}

	const requiredOptions: Required<CalculateDifferenceOptions> = {
		includeComputedProperties: true,
		includeNotOfInterestProperties: false,
		recurseIntoObjects: true,
		...options,
	}
	return calculateObjectDifferencesInternal(a, b, [], requiredOptions)
}
